<script setup lang="ts">
import { capitalize } from '@sancare/typescript-commons/src/misc/string-utils'
import { computed } from 'vue'

import { HospitalizationTypeFullName, statusLabels } from '@/misc/constants'
import {
  formatCost,
  formatGroupingCost,
  formatOptionalDate
} from '@/stay-list/stay-list-table/helpers/line/lineContentFormatters'
import { ExternalPmsiStatusLabel } from '@/stay-list/stay-list-table/helpers/line/lineHelper'
import useStayListLine from '@/stay-list/stay-list-table/line/composables/stayListLine'
import useStayRedirection from '@/stay-list/stay-list-table/line/composables/stayRedirection'
import NotificationLineColumn from '@/stay-list/stay-list-table/line/NotificationLineColumn.vue'
import { ListSmrStay } from '@/stay-list/stay-list-table/smr/types'
import { StayListLineProps } from '@/stay-list/stay-list-table/types'
import { SeenStatusEnum } from '@/store/modules/stay/types'

const props = withDefaults(defineProps<StayListLineProps<ListSmrStay>>(), {
  automationType: null
})

const {
  patientName,
  staySeen,
  stayStart,
  stayEnd,
  validationDate,
  moneyGap,
  displayedSeenStatus,
  searches,
  groupingError,
} = useStayListLine(props.stay)

const firstValue = computed(() => formatCost(props.stay.firstStandardModeGroupingResult.value))
const currentValue = computed(() => formatGroupingCost(props.stay.standardModeGroupingResult.gme !== null, props.stay.standardModeGroupingResult.value))
const rhsCount = computed(() => props.stay.rhsCount)
const initialGme = computed(() => props.stay.firstStandardModeGroupingResult.gme?.reference)
const currentGme = computed(() => props.stay.standardModeGroupingResult.gme?.reference)
const surgeryDate = computed(() => formatOptionalDate(props.stay.surgeryDate))

const { viewStay } = useStayRedirection(`/smr/id/${props.stay.id}`)

const emit = defineEmits<{
  (e: 'toggle-selected-stay', stayId: number)
}>()

function toggleSelectedStay() {
  emit('toggle-selected-stay', props.stay.id)
}
</script>

<template>
  <tr
    :class="{
      'unseen-stay': staySeen.statusValue >= SeenStatusEnum.UNSEEN,
      'selected': selected
    }"
    name="stayList_line"
    class="stay-list-line text-center align-items-center"
    @click="viewStay($event)"
    @click.middle="viewStay($event, true)"
  >
    <notification-line-column
      :displayed="'notification' in columns"
      :stay-seen="staySeen"
    />
    <td v-if="columns.legalFiness">
      {{ stay.legalEntity?.finess }}
    </td>
    <td v-if="columns.geoFiness">
      {{ stay.geographicEntity?.finess }}
    </td>
    <td v-if="columns.administrativeLocalStayId">
      {{ stay.administrativeLocalStayId }}
    </td>
    <td v-if="columns.smrId">
      {{ stay.smrId }}
    </td>
    <td v-if="columns.patientName">
      {{ patientName }}
    </td>
    <td v-if="columns.hospitalizationType">
      {{ capitalize(HospitalizationTypeFullName[stay.hospitalizationType]) }}
    </td>
    <td v-if="columns.stayStart">
      {{ stayStart }}
    </td>
    <td v-if="columns.stayEnd">
      {{ stayEnd }}
    </td>
    <td v-if="columns.initialGme">
      {{ initialGme }}
    </td>
    <td v-if="columns.initialGmeCmGn">
      {{ initialGme?.slice(0, 4) }}
    </td>
    <td v-if="columns.initialGmeReadaptationType">
      {{ initialGme?.slice(4, 5) }}
    </td>
    <td v-if="columns.initialGmeHeaviness">
      {{ initialGme?.slice(5, 6) }}
    </td>
    <td v-if="columns.initialGmeSeverity">
      {{ initialGme?.slice(6, 7) }}
    </td>
    <td v-if="columns.currentGme">
      {{ currentGme }}
    </td>
    <td v-if="columns.currentGmeCmGn">
      {{ currentGme?.slice(0, 4) }}
    </td>
    <td v-if="columns.currentGmeReadaptationType">
      {{ currentGme?.slice(4, 5) }}
    </td>
    <td v-if="columns.currentGmeHeaviness">
      {{ currentGme?.slice(5, 6) }}
    </td>
    <td v-if="columns.currentGmeSeverity">
      {{ currentGme?.slice(6, 7) }}
    </td>
    <td v-if="columns.status">
      <span>{{ statusLabels[stay.status] ? statusLabels[stay.status] : stay.status }}</span>
    </td>
    <td v-if="columns.latestSurgeryDate">
      {{ surgeryDate }}
    </td>
    <td v-if="columns.addedDasCount">
      {{ stay.addedDas.length }}
    </td>
    <td v-if="columns.addedDas">
      {{ stay.addedDas.join(',') }}
    </td>
    <td v-if="columns.firstValue">
      <span>{{ firstValue }}</span>
    </td>
    <td v-if="columns.currentValue">
      <span>{{ currentValue }}</span>
    </td>
    <td v-if="columns.moneyGap">
      <span>{{ moneyGap }}</span>
    </td>
    <td v-if="columns.heavinessMoneyGap">
      <span>{{ formatCost(stay.heavinessMoneyGap) }}</span>
    </td>
    <td v-if="columns.severityMoneyGap">
      <span>{{ formatCost(stay.severityMoneyGap) }}</span>
    </td>
    <td v-if="columns.rhsCount">
      <span>{{ rhsCount }}</span>
    </td>
    <td v-if="columns.validationDate">
      {{ validationDate }}
    </td>
    <td v-if="columns.stayDuration">
      {{ stay.stayDuration }}
    </td>
    <td v-if="columns.countSavedSearches">
      {{ searches.length }}
    </td>
    <td
      v-if="columns.savedSearches"
      :title="searches.join('\n')"
      class="saved-searches-column"
    >
      {{ searches.join(' -- ') }}
    </td>
    <td v-if="columns.externalPmsiStatus">
      <span>
        {{ ExternalPmsiStatusLabel[stay.externalPmsiStatus] || stay.externalPmsiStatus || '' }}
      </span>
    </td>
    <td
      v-if="columns.comment"
      :title="stay.comment"
    >
      <span class="freetext-wrapper">{{ stay.comment }}</span>
    </td>
    <td v-if="columns.seen">
      {{ displayedSeenStatus }}
    </td>
    <td v-if="columns.groupingError">
      {{ groupingError }}
    </td>
    <td
      @click.stop=""
      @click.middle.stop=""
    >
      <input
        v-if="allowSelection"
        :checked="selected"
        type="checkbox"
        @change="toggleSelectedStay"
      >
    </td>
  </tr>
</template>
